import React from "react"
import { graphql } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import Layout from "../components/Layout"
import MetaData from "../components/meta"
import CheckIcon from "../images/inline-svgs/check-icon-thin.svg"
import { Button } from "../elements"
import theme from "../styles/theme"
import ResourceCard from "../components/ResourceCard"

import "../styles/home.scss"
import * as styles from "./index.module.scss"
import "swiper/css"
import "swiper/css/pagination"
import "../styles/swiper-customizations.scss"

import CustomLogos from "../components/CustomLogos"

interface Props {
  data: any
}

const recordABTestViewed = ({ name, variant }) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: "ab_test",
    ab_test_name: name,
    ab_test_variant: variant,
    ab_test_page: window.location.pathname,
  })
}

const recordABTestConversion = ({ name, variant }) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: "ab_test_conversion",
    ab_test_name: name,
    ab_test_variant: variant,
    ab_test_page: window.location.pathname,
  })
}

const IndexPage = ({ data, location }: Props) => {
  let latest_blogs = data.latest_blog.edges
  let posts = latest_blogs.concat(data.latest_resource.edges)
  let featured_posts = data.featured_posts.edges.concat(posts)

  if (featured_posts.length > 3) {
    featured_posts = featured_posts.slice(3)
  }

  return (
    <Layout>
      <MetaData location={location} />
      <div className="home-page">
        <section className="bg-neutral-750 py-20 xl:pt-30 xl:pb-36 relative overflow-hidden">
          <div className="hidden lg:flex absolute top-0 left-0 right-0 justify-center">
            <StaticImage
              src="../images/home/top-pattern@3x.png"
              alt=""
              width={1224}
              height={216}
              placeholder="none"
              quality={90}
            />
          </div>
          <div className="container--boxed relative z-10">
            <header className="text-center relative z-10">
              <h1 className="text--4xl font-bold text-white mb-4">
                <span
                  className={`${styles.textGradient} bg-clip-text text-transparent`}
                >
                  Run APIs Easily.
                </span>{" "}
                <span
                  className={`${styles.textGradient2} bg-clip-text text-transparent`}
                >
                  Anywhere.
                </span>
              </h1>
              <p
                className="text-lg text-neutral-200 mx-auto"
                style={{
                  maxWidth: 702,
                }}
              >
                Cloud-native, GitOps-driven API runtime solutions for demanding
                DevOps and Platform Engineers with diverse use-cases,
                environments, and deployment models.
              </p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center max-w-sm mx-auto lg:max-w-none">
                <Button
                  elementType="link"
                  uppercase
                  large
                  bg="background.base"
                  color="text.dark"
                  hover={{
                    color: theme.colors.text.dark,
                    bg: "#ebeff7",
                  }}
                  href="https://info.traefik.io/en/request-demo"
                  width={["100%", "100%", "100%", "200px"]}
                >
                  Get a Demo
                </Button>
                <Button
                  elementType="link"
                  uppercase
                  large
                  hollow
                  color="text.inverted.base"
                  borderColor="text.inverted.light4x"
                  hover={{
                    color: theme.colors.text.dark,
                    bg: theme.colors.background.base,
                  }}
                  width={["100%", "100%", "100%", "200px"]}
                  href="https://traefik.io/pricing/"
                >
                  Compare Solutions
                </Button>
              </div>
            </header>
            <div className="mt-10 md:-mt-7 flex justify-center">
              <div className="hidden md:block">
                <StaticImage
                  src="../images/home/diagram@3x.png"
                  alt=""
                  placeholder="none"
                  quality={85}
                  objectFit="contain"
                  width={1226}
                  height={466}
                />
              </div>
              <div className="block md:hidden">
                <StaticImage
                  src="../images/home/diagram-mobile@3x.png"
                  alt=""
                  placeholder="none"
                  quality={75}
                  objectFit="contain"
                  width={400}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Customers */}
        <CustomLogos className="pb-20 lg:pb-25" />

        {/* Products */}
        <section className="home-products section--default bg-neutral-750 relative">
          <div className="container--boxed max-w-4xl mx-auto text-center relative">
            <h2 className="heading--h1 text-neutral-50">
              Start with Us. Grow with Us.
            </h2>
            <p
              className="text--lg text-neutral-200 mt-6 mx-auto"
              style={{ maxWidth: "600px" }}
            >
              Traefik Labs is with you every step of the way.
            </p>
          </div>

          <div className="container--boxed mt-20 xl:mt-36 relative">
            <div className="home-products-layout home-products-layout--desktop  ">
              <div
                id="home-products-details"
                className="products-details lg:col-span-4"
              >
                <div className="products-details-wrapper lg:block">
                  {/* Hub */}
                  <div className="mb-20 xl:mb-36 grid grid-cols-1 md:grid-cols-10 items-center justify-items-end">
                    <div className="col-span-4">
                      <h2
                        className={`inline-block heading--h2 text-neutral-50 mb-4.5 bg-clip-text text-transparent ${styles.proxyTextGradient}`}
                      >
                        Application Proxy
                      </h2>
                      <div className="text-neutral-200 mb-8">
                        When DevOps and platform engineering teams need to
                        simplify and automate the discovery, routing, and load
                        balancing of microservices and virtual machines, an
                        application proxy is a great choice. Our application
                        proxy is fully-declarative, highly-performant, and can
                        handle a diverse set of application environments.
                      </div>
                      <ul className="mb-8 text-proxy-600">
                        <HeroLi>Services Auto Discovery</HeroLi>
                        <HeroLi>Lightweight & Cloud-Native</HeroLi>
                        <HeroLi>
                          Advanced Load Balancing (Canary, Blue/Green, etc.)
                        </HeroLi>
                        <HeroLi>
                          Fully Declarative & GitOps Operating Model
                        </HeroLi>
                        <HeroLi>Integrates with all Major Orchestrators</HeroLi>
                        <HeroLi>Coraza Web Application Firewall (WAF)</HeroLi>
                      </ul>
                      <div className="flex">
                        <Button
                          display="inline-flex"
                          elementType="link"
                          hollow
                          color="#050a22"
                          borderColor="#fff"
                          bg="#fff"
                          hover={{
                            color: "#050a22",
                            bg: "#e5e8ea",
                          }}
                          width={["auto"]}
                          href="/traefik"
                          arrow
                        >
                          Learn more
                        </Button>
                      </div>
                    </div>
                    <div className="md:col-span-6">
                      <StaticImage
                        className="ml-auto"
                        src="../images/home/products/diagrams-application-proxy.png"
                        alt="Traefik Proxy Diagram"
                        placeholder="none"
                        quality={90}
                        width={642}
                        height={450}
                      />
                    </div>
                  </div>

                  <div className="mb-20 xl:mb-36 grid grid-cols-1 md:grid-cols-10 items-center justify-items-end">
                    <div className="col-span-4">
                      <h2
                        className={`inline-block heading--h2 text-neutral-50 mb-4.5 bg-clip-text text-transparent ${styles.gatewayTextGradient}`}
                      >
                        API Gateway
                      </h2>
                      <div className="text-neutral-200 mb-8">
                        When security and centralized control become priorities,
                        teams need to upgrade to an API gateway. Upgrade our
                        application proxy seamlessly to a fully declarative,
                        highly scalable, enterprise-grade API gateway with
                        out-of-the-box integrations with leading AuthN, AuthZ,
                        and Key management solutions.
                      </div>
                      <ul className="mb-8 text-[#bb64f9]">
                        <HeroLi>
                          <span className="font-bold text-white">
                            Includes All Application Proxy Capabilities +
                          </span>
                        </HeroLi>
                        <HeroLi>Centralized Control & Governance</HeroLi>
                        <HeroLi>
                          Authentication and Authorization (OIDC, OAuth, etc.)
                        </HeroLi>
                        <HeroLi>
                          Distributed Security Features (Let’s Encrypt, Rate
                          Limiting, etc.)
                        </HeroLi>
                        <HeroLi>
                          Enterprise-grade Key Management (HashiCorp Vault &
                          Azure Key Vault)
                        </HeroLi>
                      </ul>
                      <div>
                        <Button
                          display="inline-flex"
                          elementType="link"
                          hollow
                          color="#050a22"
                          borderColor="#fff"
                          bg="#fff"
                          hover={{
                            color: "#050a22",
                            bg: "#e5e8ea",
                          }}
                          width={["auto"]}
                          href="/traefik-hub-api-gateway/"
                          arrow
                        >
                          Learn more
                        </Button>
                      </div>
                    </div>
                    <div className="md:col-span-6">
                      <StaticImage
                        className="ml-auto"
                        src="../images/home/products/diagrams-api-gateway.png"
                        alt="API Gateway illustration"
                        quality={90}
                        width={642}
                        height={450}
                        placeholder="none"
                      />
                    </div>
                  </div>

                  {/* Enterprise */}
                  <div className="grid grid-cols-1 md:grid-cols-10 items-center justify-items-end">
                    <div className="col-span-4">
                      <h2
                        className={`inline-block heading--h2 text-neutral-50 mb-4.5 bg-clip-text text-transparent ${styles.managementTextGradient}`}
                      >
                        API Management
                      </h2>
                      <div className="text-neutral-200 mb-8">
                        As API lifecycle management becomes a priority, teams
                        need additional capabilities beyond what API gateways
                        can provide. Seamlessly add the control plane and API
                        lifecycle management capabilities to your existing
                        Traefik deployment. Our API management is
                        fully-declarative, highly intuitive, and highly
                        customizable, delivering differentiated experiences for
                        API consumers and publishers.
                      </div>
                      <ul className="mb-8 text-hub-500">
                        <HeroLi>
                          <span className="font-bold text-white">
                            Includes All API Gateway Capabilities +
                          </span>
                        </HeroLi>
                        <HeroLi>
                          Centralized Control Plane with Distributed API Gateway
                          Data Plane
                        </HeroLi>
                        <HeroLi>
                          API Discovery, Collections, & Versioning
                        </HeroLi>
                        <HeroLi>
                          API Linting (for Breaking Changes & Error Detection)
                        </HeroLi>
                        <HeroLi>API Granular Access Control</HeroLi>
                        <HeroLi>API Usage, Monitoring, & Observability</HeroLi>
                      </ul>
                      <div>
                        <Button
                          display="inline-flex"
                          elementType="link"
                          hollow
                          color="#050a22"
                          borderColor="#fff"
                          bg="#fff"
                          hover={{
                            color: "#050a22",
                            bg: "#e5e8ea",
                          }}
                          width={["auto"]}
                          href="/traefik-hub"
                          arrow
                        >
                          Learn more
                        </Button>
                      </div>
                    </div>
                    <div className="md:col-span-6">
                      <StaticImage
                        className="ml-auto"
                        src="../images/home/products/diagrams-api-management.png"
                        alt="API Management illustration"
                        quality={90}
                        width={642}
                        height={450}
                        placeholder="none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className="hidden lg:block lg:col-span-6">
                <div id="home-products-images" className="products-images">
                  <div className="products-images-wrapper ">
                    <StaticImage
                      className=" products-images-item products-images-item--hub ml-auto"
                      src="../images/home/products/diagrams-application-proxy.png"
                      alt="Traefik mesh illustration"
                      placeholder="none"
                      quality={90}
                      width={642}
                      height={450}
                    />
                    <StaticImage
                      className="products-images-item products-images-item--traefikee ml-auto"
                      src="../images/home/products/diagrams-api-management.png"
                      alt="Traefik Enterprise illustration"
                      quality={90}
                      width={642}
                      height={450}
                    />
                    <StaticImage
                      className="products-images-item products-images-item--maesh ml-auto"
                      src="../images/home/products/diagrams-api-gateway.png"
                      alt="Traefik Hub illustration"
                      quality={90}
                      width={642}
                      height={450}
                    />
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </section>

        {featured_posts && featured_posts.length > 0 && (
          <section className="section--default bg-white">
            <div className="container--boxed">
              <h2 className="heading--h2 text-center">
                Latest from Traefik Labs
              </h2>
              <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-15 gap-x-6 justify-items-center">
                {featured_posts.map(({ node }) => (
                  <ResourceCard
                    key={node.id}
                    data={node}
                    styles={{
                      linkColor: "text.dark",
                      eyebrowColor: "text.light",
                    }}
                    headingVariant="h3"
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        <section
          className="relative overflow-hidden section--default bg-neutral-700"
          id="get-started"
        >
          <div className="absolute top-0 left-0 w-full h-full">
            <StaticImage
              src="../images/home/cta/bg-cta@3x.png"
              alt=""
              layout="fullWidth"
              className="w-full h-full"
              objectFit="cover"
              objectPosition="top center"
              quality={90}
            />
          </div>
          <div className="relative flex flex-col items-center text-center container--boxed">
            <h2 className="heading--h1 text-neutral-50">Get Started today</h2>

            <div className="flex flex-col items-center w-full max-w-xs mt-10 space-y-4 sm:flex-row sm:justify-center sm:space-y-0 sm:space-x-4 sm:max-w-none">
              <Button
                elementType="link"
                uppercase
                large
                bg="background.base"
                color="text.dark"
                hover={{
                  color: theme.colors.text.dark,
                  bg: "#ebeff7",
                }}
                href="https://info.traefik.io/en/request-demo"
                width={["100%", "214px"]}
              >
                Get a Demo
              </Button>
              <Button
                elementType="link"
                uppercase
                large
                hollow
                color="text.inverted.base"
                borderColor="text.inverted.base"
                hover={{
                  color: theme.colors.text.dark,
                  bg: theme.colors.background.base,
                }}
                width={["100%", "auto"]}
                href="https://info.traefik.io/speak-with-an-expert"
              >
                Speak with an expert
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

const HeroLi = ({ children }) => (
  <li className="flex text-current mt-3 first:mt-0">
    <span className="min-w-8 ">
      <CheckIcon className=" text-current" />
    </span>
    <span className="text-neutral-50">{children}</span>
  </li>
)

export default IndexPage

export const query = graphql`
  {
    featured_posts: allGhostPost(
      filter: {
        tags: { elemMatch: { slug: { eq: "hash-homepage-featured-post" } } }
      }
      sort: { published_at: DESC }
      limit: 3
    ) {
      edges {
        node {
          ...GhostCarouselCardFields
        }
      }
    }
    latest_blog: allGhostPost(
      filter: { primary_tag: { slug: { eq: "blog" } } }
      sort: { published_at: DESC }
      limit: 2
    ) {
      edges {
        node {
          ...GhostCarouselCardFields
        }
      }
    }
    latest_resource: allGhostPost(
      filter: { primary_tag: { slug: { eq: "resources" } } }
      sort: { published_at: DESC }
      limit: 1
    ) {
      edges {
        node {
          ...GhostCarouselCardFields
        }
      }
    }
  }
`
